import type { Post } from '@/types';

export interface VideoSource {
  src: string;
  poster: string;
  isHls: boolean;
  isPortrait: boolean;
}

/**
 * Extract video source, poster, and HLS flag from a Post.
 * Returns null if no playable video URL is found.
 */
export function getVideoSource(post: Post): VideoSource | null {
  // 1. Try SDK-normalised videoUrls (preferred)
  const hls = post.videoUrls?.hls;
  const mp4 = post.videoUrls?.mp4;

  // 2. Fallback to raw API videos[] array
  const videos = (post as Record<string, unknown>).videos as
    | Array<Record<string, unknown>>
    | undefined;
  const rawUrl = videos?.[0]?.url as string | undefined;

  const src = hls ?? mp4 ?? rawUrl;
  if (!src) return null;

  // Poster: videoUrls.thumbnail → videos[].thumbnail.url → first image
  const poster =
    post.videoUrls?.thumbnail ??
    ((videos?.[0]?.thumbnail as Record<string, unknown> | undefined)?.url as string | undefined) ??
    getFirstImageUrl(post) ??
    '';

  const isHls = src.endsWith('.m3u8');

  // Detect portrait (9:16) from video or thumbnail dimensions
  const firstVideo = videos?.[0];
  const w = (firstVideo?.widthPx as number) ?? (firstVideo?.width as number) ?? 0;
  const h = (firstVideo?.heightPx as number) ?? (firstVideo?.height as number) ?? 0;
  const isPortrait = w > 0 && h > 0 ? h > w : false;

  return { src, poster, isHls, isPortrait };
}

function getFirstImageUrl(post: Post): string | undefined {
  const images = post.images as Array<Record<string, unknown>> | undefined;
  if (images && images.length > 0) {
    return (images[0]?.url as string) || (images[0] as unknown as string);
  }
  return undefined;
}
